import {
  Button,
  Checkbox,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@simplicity/ui";
import { Plus } from "lucide-react";
import { useId, useState } from "react";

/**
 * Add tables to one schedule (SIM-3402). Add-only on purpose: removing a table destroys its
 * settings, so that lives in the roster where the summary says what would be lost. Tables
 * already on the schedule are context, not uncheckable rows.
 */

export interface AddTablesPickerProps {
  scheduleName: string;
  /** Every table on the connector — `vars.tables` names. */
  tableNames: string[];
  /** Names already on this schedule. */
  onSchedule: string[];
  onAdd: (names: string[]) => void;
}

function matches(name: string, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  return trimmed === "" || name.toLowerCase().includes(trimmed);
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-2 first:mt-0">
      <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">{label}</p>
      {children}
    </div>
  );
}

function PickerBody({ scheduleName, tableNames, onSchedule, onAdd }: AddTablesPickerProps) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const groupId = useId();

  const member = new Set(onSchedule);
  const available = tableNames.filter((n) => !member.has(n) && matches(n, query));
  const present = tableNames.filter((n) => member.has(n) && matches(n, query));

  function toggle(name: string) {
    setPicked((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }

  return (
    <div className="flex max-h-96 flex-col">
      <Input
        size="sm"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search tables..."
        aria-label={`Filter tables to add to ${scheduleName}`}
        className="mb-2"
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Group label="Not on this schedule">
          {available.length === 0 ? (
            <p className="px-1 py-1 text-xs text-muted-foreground">
              {tableNames.length === 0
                ? "This connector has no tables yet."
                : "Every table is already on this schedule."}
            </p>
          ) : (
            available.map((name) => (
              <Label
                key={name}
                htmlFor={`${groupId}-${name}`}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm font-normal hover:bg-accent"
              >
                <Checkbox
                  id={`${groupId}-${name}`}
                  checked={picked.includes(name)}
                  onCheckedChange={() => toggle(name)}
                />
                <span className="min-w-0 flex-1 truncate">{name}</span>
              </Label>
            ))
          )}
        </Group>
        {present.length > 0 && (
          <Group label={`On this schedule (${present.length})`}>
            <ul className="px-1 text-sm text-muted-foreground">
              {present.map((name) => (
                <li key={name} className="truncate py-1">
                  {name}
                </li>
              ))}
            </ul>
            <p className="px-1 pt-1 text-xs text-muted-foreground">
              Remove these from the list above the picker, where their settings are shown.
            </p>
          </Group>
        )}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-2 shrink-0"
        disabled={picked.length === 0}
        onClick={() => {
          onAdd(picked);
          setPicked([]);
          setQuery("");
        }}
      >
        {picked.length === 0
          ? "Add tables"
          : `Add ${picked.length} table${picked.length === 1 ? "" : "s"}`}
      </Button>
    </div>
  );
}

export function AddTablesPicker(props: AddTablesPickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* Same outline/sm/text-xs shape as "Add table" and "Add schedule" — it does the
            same job one level down, and a ghost button read as body text beside them. */}
        <Button type="button" variant="outline" size="sm" className="mt-1 gap-1 text-xs">
          <Plus className="h-3 w-3" aria-hidden="true" />
          {`Add tables to ${props.scheduleName}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <PickerBody {...props} />
      </PopoverContent>
    </Popover>
  );
}
